// FAQ 事前登録 (spec §4.6 補助)。
// 追加 / 削除に加えて AI による候補提案を統合 (host が研修テーマを入力 →
// Anthropic で候補生成 → 選択して保存)。

import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import type { Faq } from '@solo-webinar-copilot/shared';
import {
  aiSuggestFaqs,
  createFaq,
  deleteFaq,
  listFaqs,
  type FaqCandidate,
} from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

export interface FaqsProps {
  ctx: AppContext;
  locale: Locale;
}

export function Faqs({ ctx, locale }: FaqsProps): ReactElement {
  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = useCallback(async () => {
    const r = await listFaqs(ctx);
    if (r.ok) {
      setItems(r.items);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [ctx]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (id: string) => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('in_client.host.faqs.confirm_delete', locale))
    ) {
      return;
    }
    const r = await deleteFaq(ctx, id);
    if (r.ok) setItems((prev) => prev.filter((x) => x.id !== id));
    else setError(r.error);
  };

  return (
    <section data-testid="faqs">
      <h2>{t('in_client.host.faqs.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="faq-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {loading ? (
        <p data-testid="faq-loading">…</p>
      ) : items.length === 0 ? (
        <p data-testid="faq-empty">{t('in_client.host.faqs.empty', locale)}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((f) => (
            <li
              key={f.id}
              data-testid={`faq-item-${f.id}`}
              style={{ marginBottom: 6 }}
            >
              <div>
                <strong>Q:</strong> {f.question}
              </div>
              <div>
                <strong>A:</strong> {f.answer}
              </div>
              <button
                type="button"
                onClick={() => void handleDelete(f.id)}
                data-testid={`faq-delete-${f.id}`}
              >
                {t('in_client.host.faqs.delete', locale)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <FaqForm
          locale={locale}
          onCancel={() => setShowForm(false)}
          onSubmit={async (draft) => {
            const r = await createFaq(ctx, draft);
            if (r.ok) {
              setItems((prev) => [...prev, r.item]);
              setShowForm(false);
            } else {
              setError(r.error);
            }
          }}
        />
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="faq-add-button"
          >
            {t('in_client.host.faqs.add', locale)}
          </button>
        </div>
      )}

      <FaqAiSuggester
        ctx={ctx}
        locale={locale}
        onSaved={(items) => {
          setItems((prev) => [...prev, ...items]);
        }}
        onError={(msg) => setError(msg)}
      />
    </section>
  );
}

interface FaqAiSuggesterProps {
  ctx: AppContext;
  locale: Locale;
  onSaved: (items: Faq[]) => void;
  onError: (msg: string) => void;
}

function FaqAiSuggester({
  ctx,
  locale,
  onSaved,
  onError,
}: FaqAiSuggesterProps): ReactElement {
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [candidates, setCandidates] = useState<FaqCandidate[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    const r = await aiSuggestFaqs(ctx, topic.trim());
    setGenerating(false);
    if (r.ok) {
      setCandidates(r.candidates);
      setSelected(new Set(r.candidates.map((_, i) => i)));
    } else {
      onError(r.error);
    }
  };

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleSaveSelected = async () => {
    const picked = candidates.filter((_, i) => selected.has(i));
    const saved: Faq[] = [];
    for (const c of picked) {
      const r = await createFaq(ctx, c);
      if (r.ok) saved.push(r.item);
    }
    onSaved(saved);
    setCandidates([]);
    setSelected(new Set());
    setTopic('');
  };

  return (
    <div
      data-testid="faq-ai-suggester"
      style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}
    >
      <h3>
        {t('in_client.host.faqs.ai_section', locale)}{' '}
        <small style={{ opacity: 0.7 }}>
          [{t('in_client.ai.generated_label', locale)}]
        </small>
      </h3>
      <div>
        <label>
          {t('in_client.host.faqs.ai_topic', locale)}
          <input
            type="text"
            value={topic}
            maxLength={2000}
            onChange={(e) => setTopic(e.currentTarget.value)}
            data-testid="faq-ai-topic"
            style={{ width: '100%' }}
          />
        </label>
      </div>
      <button
        type="button"
        onClick={() => void handleGenerate()}
        disabled={generating || !topic.trim()}
        data-testid="faq-ai-generate"
      >
        {generating
          ? t('in_client.host.faqs.ai_generating', locale)
          : t('in_client.host.faqs.ai_generate', locale)}
      </button>
      {candidates.length > 0 ? (
        <div data-testid="faq-ai-candidates">
          <h4>{t('in_client.host.faqs.ai_candidates', locale)}</h4>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {candidates.map((c, i) => (
              <li
                key={i}
                data-testid={`faq-ai-candidate-${i}`}
                style={{ marginBottom: 4 }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggle(i)}
                    data-testid={`faq-ai-candidate-check-${i}`}
                  />{' '}
                  <strong>Q:</strong> {c.question}
                  <br />
                  <span style={{ marginLeft: 24 }}>
                    <strong>A:</strong> {c.answer}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => void handleSaveSelected()}
            disabled={selected.size === 0}
            data-testid="faq-ai-save-selected"
          >
            {t('in_client.host.faqs.ai_save_selected', locale)}
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface FaqFormProps {
  locale: Locale;
  onCancel: () => void;
  onSubmit: (draft: { question: string; answer: string }) => Promise<void>;
}

function FaqForm({ locale, onCancel, onSubmit }: FaqFormProps): ReactElement {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ question: question.trim(), answer: answer.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} data-testid="faq-form">
      <div>
        <label>
          {t('in_client.host.faqs.question', locale)}
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.currentTarget.value)}
            data-testid="faq-form-question"
            required
          />
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.faqs.answer', locale)}
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.currentTarget.value)}
            data-testid="faq-form-answer"
            required
            rows={4}
          />
        </label>
      </div>
      <div>
        <button type="submit" disabled={submitting} data-testid="faq-form-save">
          {t('in_client.host.faqs.save', locale)}
        </button>
        <button type="button" onClick={onCancel} data-testid="faq-form-cancel">
          {t('in_client.host.faqs.cancel', locale)}
        </button>
      </div>
    </form>
  );
}
